import { useEffect, useMemo, useState } from 'react';
import styles from './ZoneLandCharacteristics.module.css';
import { getZoneLandData } from '../../mapLayers/lib/zoneLandData';
import GradedLayerRow from './GradedLayerRow.js';
import LandSummaryRow from './LandSummaryRow.js';
import FloodSummaryRow from './FloodSummaryRow.js';
import {
  aggregateLandSummary,
  hasLandSummaryContent,
} from './aggregateLandSummary.js';

/*
    ZONE LAND CHARACTERISTICS (section for the Zone Details panel)
        Loads land information for a zone and renders consolidated summary rows.
        props:
            zone: zone document (polygon_shape and/or center_point)
*/

const STRINGS = {
  header: 'Land characteristics',
  drawBoundaryHint: 'Draw a field boundary to see land information for this zone.',
  pointMarkerHint:
    'Draw the field boundary for more accurate insights. A point marker gives a rough check only.',
  loading: 'Checking land information…',
  fetchError: "We couldn't check land information right now.",
  noLayers: 'No land information is available for this field yet.',
  rowError: "We couldn't check this right now.",
};

function hasBoundary(zone) {
  return Boolean(zone?.polygon_shape?.coordinates?.length);
}

function hasCenterPoint(zone) {
  return Boolean(zone?.center_point?.coordinates?.length === 2);
}

function summaryChipVariant(label, noneLabel) {
  return label === noneLabel ? 'none' : 'in';
}

function ZoneLandCharacteristics(props) {
  const { zone } = props;
  const [state, setState] = useState({ status: 'loading', data: null });

  // Reshapes already-fetched layer data into summary rows; recompute only when
  // the fetched data changes, not on every render.
  const summary = useMemo(() => aggregateLandSummary(state.data), [state.data]);

  useEffect(() => {
    if (!hasBoundary(zone)) {
      setState({ status: 'no-boundary', data: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', data: null });
    getZoneLandData(zone)
      .then((data) => {
        if (cancelled) return;
        if (!data || data.status === 'no-polygon') {
          setState({ status: 'no-boundary', data: null });
        } else {
          setState({ status: 'ready', data });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [zone]);

  const renderGradedRow = (entry, options = {}) => {
    if (!entry) {
      return null;
    }
    const { result, title } = entry;
    if (result.status === 'error') {
      return (
        <div key={entry.layerId} className={styles.errorRow}>
          <span className={styles.errorRowTitle}>{title}</span>
          <span className={styles.errorRowText}>{STRINGS.rowError}</span>
        </div>
      );
    }
    if (result.status !== 'ok') {
      return null;
    }
    return (
      <GradedLayerRow
        key={entry.layerId}
        title={title}
        result={result}
        source={entry.source}
        learnMore={options.learnMore}
      />
    );
  };

  const renderSummaryRows = () => {
    if (!hasLandSummaryContent(summary)) {
      return <p className={styles.hint}>{STRINGS.noLayers}</p>;
    }

    return (
      <>
        {renderGradedRow(summary.soil, { learnMore: summary.soil?.learnMore })}
        {renderGradedRow(summary.landCapability)}
        {summary.nvz?.label ? (
          <LandSummaryRow
            title={summary.nvz.title}
            value={summary.nvz.label}
            variant={summaryChipVariant(summary.nvz.label, 'Not in an NVZ')}
            sourceLink={summary.nvz.sourceLink}
          />
        ) : summary.nvz?.status === 'error' ? (
          <div className={styles.errorRow}>
            <span className={styles.errorRowTitle}>{summary.nvz.title}</span>
            <span className={styles.errorRowText}>{STRINGS.rowError}</span>
          </div>
        ) : null}
        {summary.designation ? (
          <LandSummaryRow
            title={summary.designation.title}
            value={summary.designation.label}
            // 'unknown' (a covering source errored, no confirmed hit) reads as
            // plain text, not a positive/negative chip.
            variant={
              summary.designation.status === 'unknown'
                ? 'plain'
                : summaryChipVariant(summary.designation.label, 'None')
            }
          />
        ) : null}
        {summary.flood?.label ? (
          <FloodSummaryRow
            title={summary.flood.title}
            label={summary.flood.label}
            sourceLink={summary.flood.sourceLink}
            severity={summary.flood.severity}
            breakdown={summary.flood.breakdown}
          />
        ) : summary.flood?.status === 'error' ? (
          <div className={styles.errorRow}>
            <span className={styles.errorRowTitle}>{summary.flood.title}</span>
            <span className={styles.errorRowText}>{STRINGS.rowError}</span>
          </div>
        ) : null}
      </>
    );
  };

  const renderContent = () => {
    if (state.status === 'no-boundary') {
      const hint = hasCenterPoint(zone) ? STRINGS.pointMarkerHint : STRINGS.drawBoundaryHint;
      return <p className={styles.hint}>{hint}</p>;
    }
    if (state.status === 'loading') {
      return <p className={styles.hint}>{STRINGS.loading}</p>;
    }
    if (state.status === 'error') {
      return <p className={styles.hint}>{STRINGS.fetchError}</p>;
    }
    return <div className={styles.rows}>{renderSummaryRows()}</div>;
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3>{STRINGS.header}</h3>
      </div>
      {renderContent()}
    </div>
  );
}

export default ZoneLandCharacteristics;
